import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom-v5-compat";
import { AirExchangeForTypeEnum } from "@b2bportal/air-exchange-api";
import {
  buildFlightShopQuery,
  CoreFlightExchangeSteps,
  FlightExchangePolicySelectors,
  FlightExchangeSearch,
  FlightPostBookingSelectors,
  FlightSearchSelectors,
  type FlightsExchangeAppProps,
  FlightShopSelectors,
  type FlightState,
  setSearchFormFromItinerary,
  setExchangeFor,
  setReturnSelection,
} from "@b2bportal/core-flights";
import {
  type AppDispatch,
  FlightBookQueryParams,
  type NavigationFlow,
  TripCategory,
} from "@b2bportal/core-types";
import {
  FLIGHT_ROUTES,
  FLIGHT_SHOP_ROUTES,
  FLIGHTS_EXCHANGE_PATH,
  useDeviceType,
} from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ReviewFarePage, ReviewPageRoute } from "@tenant/flights";
import { ExchangeDeparturePageRoute } from "../pages/ExchangeDeparturePage";
import { ExchangeReturnPageRoute } from "../pages/ExchangeReturnPage";

export const useCoreFlightsExchangeFlow = (
  props: FlightsExchangeAppProps
): NavigationFlow<CoreFlightExchangeSteps> => {
  const { CheckoutPage: FlightExchangeCheckout } = props;
  const { t } = useI18nContext();
  const { isMobile, isTablet } = useDeviceType();
  const dispatch = useDispatch<AppDispatch>();
  const { itineraryId = "" } = useParams();

  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);
  const isRoundTrip = tripCategory === TripCategory.ROUND_TRIP;
  const selectedTrip = useSelector(FlightShopSelectors.getSelectedTrip);
  const exchangePolicy = useSelector(
    FlightExchangePolicySelectors.getExchangePolicy
  );
  const selectedItinerary = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  );
  const airports = useSelector(FlightPostBookingSelectors.getAirports);

  useEffect(() => {
    if (selectedItinerary.data && exchangePolicy) {
      dispatch(
        setSearchFormFromItinerary({
          airports,
          exchangeScenario: exchangePolicy,
          itinerary: selectedItinerary.data,
        })
      );

      // Set exchangeFor based on itinerary trip type (one-way vs round-trip)
      const sliceCount =
        selectedItinerary.data.bookedItinerary.travelItinerary.slices.length;
      const isOneWay = sliceCount === 1;
      const exchangeForType = isOneWay
        ? AirExchangeForTypeEnum.OneWay
        : AirExchangeForTypeEnum.RoundTrip;

      dispatch(setExchangeFor(exchangeForType));

      // For one-way trips, set returnSelection to undefined (no return flight to exchange)
      if (isOneWay) {
        dispatch(setReturnSelection(undefined));
      }
    }
  }, [airports, dispatch, exchangePolicy, selectedItinerary.data]);

  const searchParams = useCallback((state: FlightState) => {
    const validSearch = FlightSearchSelectors.getValidSearchState(state);
    if (validSearch == null) {
      return undefined;
    }
    return buildFlightShopQuery({
      ...validSearch,
      departureDate: new Date(validSearch.departureDate),
      ...(validSearch.tripCategory === TripCategory.ROUND_TRIP
        ? {
            returnDate: new Date(validSearch.returnDate),
          }
        : {}),
    });
  }, []);

  const homeStep = useMemo(
    () => ({
      [CoreFlightExchangeSteps.HOME]: {
        id: CoreFlightExchangeSteps.HOME,
        relativePath: "/",
        component: <FlightExchangeSearch />,
        show: true,
      },
    }),
    []
  );

  const departureStep = useMemo(() => {
    return {
      [CoreFlightExchangeSteps.DEPARTURE]: {
        id: CoreFlightExchangeSteps.DEPARTURE,
        relativePath: `${FLIGHT_ROUTES.SHOP_ROUTE}/${FLIGHT_SHOP_ROUTES.DEPARTURE_ROUTE}`,
        queryParams: searchParams,
        component: <ExchangeDeparturePageRoute />,
        show: true,
        breadcrumb: {
          label: t("flightShopProgress.departure"),
        },
      },
    };
  }, [searchParams, t]);

  const returnStep = useMemo(() => {
    return {
      [CoreFlightExchangeSteps.RETURN]: {
        id: CoreFlightExchangeSteps.RETURN,
        relativePath: `${FLIGHT_ROUTES.SHOP_ROUTE}/${FLIGHT_SHOP_ROUTES.RETURN_ROUTE}`,
        queryParams: searchParams,
        component: <ExchangeReturnPageRoute />,
        show: isRoundTrip,
        breadcrumb: {
          label: t("flightShopProgress.return"),
          show: isRoundTrip,
        },
      },
    };
  }, [isRoundTrip, searchParams, t]);

  const reviewStep = useMemo(
    () => ({
      [CoreFlightExchangeSteps.REVIEW]: {
        id: CoreFlightExchangeSteps.REVIEW,
        relativePath: `${FLIGHT_ROUTES.SHOP_ROUTE}/${FLIGHT_SHOP_ROUTES.REVIEW_ROUTE}`,
        component: <ReviewPageRoute />,
        queryParams: searchParams,
        show: true,
        breadcrumb: {
          label: t("flightShopProgress.reviewItinerary"),
        },
      },
    }),
    [searchParams, t]
  );

  const reviewFareStep = useMemo(
    () => ({
      [CoreFlightExchangeSteps.REVIEW_FARE]: {
        id: CoreFlightExchangeSteps.REVIEW_FARE,
        relativePath: `${FLIGHT_ROUTES.SHOP_ROUTE}/${FLIGHT_SHOP_ROUTES.REVIEW_ROUTE}/${FLIGHT_SHOP_ROUTES.REVIEW_FARE_ROUTE}`,
        component: <ReviewFarePage />,
        queryParams: searchParams,
        show: isMobile || isTablet,
      },
    }),
    [isMobile, isTablet, searchParams]
  );

  const checkoutStep = useMemo(() => {
    const params = new URLSearchParams(
      Object.entries({
        [FlightBookQueryParams.TRIP_ID]: selectedTrip.tripId,
        [FlightBookQueryParams.OUTGOING_FARE_ID]: selectedTrip.outgoingFareId,
        [FlightBookQueryParams.RETURN_FARE_ID]: selectedTrip.returnFareId,
      }).reduce(
        (acc, [key, value]) => {
          if (value != null) acc[key] = value;
          return acc;
        },
        {} as Record<string, string>
      )
    );

    return {
      [CoreFlightExchangeSteps.CHECKOUT]: {
        id: CoreFlightExchangeSteps.CHECKOUT,
        relativePath: `${FLIGHT_ROUTES.BOOK_ROUTE}`,
        component: FlightExchangeCheckout,
        queryParams: params.toString(),
        show: true,
      },
    };
  }, [FlightExchangeCheckout, selectedTrip]);

  return useMemo(
    () => ({
      stepOrder: [
        CoreFlightExchangeSteps.HOME,
        CoreFlightExchangeSteps.DEPARTURE,
        CoreFlightExchangeSteps.RETURN,
        CoreFlightExchangeSteps.REVIEW,
        CoreFlightExchangeSteps.REVIEW_FARE,
        CoreFlightExchangeSteps.CHECKOUT,
      ],
      steps: {
        ...homeStep,
        ...departureStep,
        ...returnStep,
        ...reviewStep,
        ...reviewFareStep,
        ...checkoutStep,
      },
      funnelPath: FLIGHTS_EXCHANGE_PATH(itineraryId),
    }),
    [
      checkoutStep,
      departureStep,
      homeStep,
      itineraryId,
      returnStep,
      reviewFareStep,
      reviewStep,
    ]
  );
};
